import { ChangeDetectionStrategy, Component, inject, signal, viewChild, WritableSignal } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { IContactAddress } from '@appShared/interfaces/[Model-based]/contact-address.interface'
import { IPaymentInstrumentSubmission } from '@appShared/interfaces/[Model-based]'
import { CommonService, ToastrType } from '@appShared/services'
import { AddressType_ } from '@appShared/services/lookup/[CodeGen]/address-type.domain'
import { PaymentInstrumentType, PaymentInstrumentType_ } from '../../../shared/services/lookup/[CodeGen]/payment-instrument-type.domain'
import { AccountDetailStore } from '../account-detail/account-detail.store'
import { BillingAddressComponent } from '../billing-address/billing-address.component'
import { EftRecordComponent } from '../eft-record/eft-record.component'
import { PaymentCardComponent } from '../payment-card/payment-card.component'

@Component({
   selector: 'app-payment-method-modal',
   templateUrl: './payment-method-modal.component.html',
   styleUrls: ['./payment-method-modal.component.less'],
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [BillingAddressComponent, EftRecordComponent, PaymentCardComponent]
})
export class PaymentMethodModalComponent {
   public readonly PaymentInstrumentType_ = PaymentInstrumentType_
   public readonly supportedPaymentInstrumentTypes: readonly PaymentInstrumentType_[] = [
      PaymentInstrumentType_.EftRecord,
      PaymentInstrumentType_.PaymentCard
   ]
   public readonly paymentInstrumentType: WritableSignal<PaymentInstrumentType_> = signal<PaymentInstrumentType_>(PaymentInstrumentType_.EftRecord)
   private readonly _eftRecord = viewChild(EftRecordComponent)
   private readonly _paymentCard = viewChild(PaymentCardComponent)
   private _methodForm: FormGroup | null = null
   private _addressForm: FormGroup | null = null

   private readonly _store = inject(AccountDetailStore)
   private readonly _common = inject(CommonService)

   /** Set when a submit fails; shown as an inline alert at the bottom of the modal. */
   public readonly errorMessage = signal<string | null>(null)
   public readonly isSubmitting = this._store.paymentInstrumentIsSubmitting

   constructor(public readonly activeModal: NgbActiveModal) {}

   selectPaymentInstrumentType(type: PaymentInstrumentType_): void {
      this.paymentInstrumentType.set(type)
      // method-specific child form will re-emit when re-instantiated; address form persists
      this._methodForm = null
   }

   getDescription(type: PaymentInstrumentType_): string {
      return PaymentInstrumentType.toDomainModel(type).description
   }

   onMethodFormReady(form: FormGroup): void {
      this._methodForm = form
   }

   onAddressFormReady(form: FormGroup): void {
      this._addressForm = form
   }

   isSaveDisabled(): boolean {
      return this.isSubmitting()
         || !this._methodForm || this._methodForm.invalid
         || !this._addressForm || this._addressForm.invalid
   }

   async save(): Promise<void> {
      if (!this._methodForm || !this._addressForm) return

      this._methodForm.markAllAsTouched()
      this._addressForm.markAllAsTouched()
      if (this._methodForm.invalid || this._addressForm.invalid) return

      const type = this.paymentInstrumentType()
      const addressValue = this._addressForm.getRawValue()

      const billingAddress: IContactAddress = {
         ...addressValue,
         typeCode: AddressType_.Billing
      } as IContactAddress

      const submission = {
         billingAddress,
         eft: type === PaymentInstrumentType_.EftRecord ? this._eftRecord()?.getSubmission() ?? null : null,
         card: type === PaymentInstrumentType_.PaymentCard ? this._paymentCard()?.getSubmission() ?? null : null
      } as IPaymentInstrumentSubmission

      // Submit BEFORE closing — only close on success; keep the modal open on failure.
      this.errorMessage.set(null)
      const result = await this._store.submitPaymentInstrument(submission)
      if (result.ok) {
         this._common.messageUser('<div class="text-center">Payment Method Added</div>')
         this.activeModal.close(submission)
      } else {
         const message = result.error ?? 'Unable to save the payment method. Please try again.'
         this.errorMessage.set(message)
         this._common.messageUser(message, 'Payment Method Failed', ToastrType.error)
      }
   }

   cancel(): void {
      this.activeModal.dismiss('cancel')
   }
}
